/**
 * Every /overview read of spec §2.2 under its run key: the period reads on
 * scope + both windows; the workspace-only reads on the workspace alone. A
 * channel, objective or currency change is applied by buildOverviewView on
 * the client and reads nothing (R20). In the master's all-workspaces view the
 * workspace-bound reads (mapping, targets, coverage, backfill, freshness) are
 * not made, and the central Result definition applies (R6, R10).
 *
 * @module hooks/useOverviewData
 */
import { useMemo } from 'react';
import { bangkokToday } from '@/services/looker/lookerDateRange';
import { loadBackfill, loadChannelFreshness } from '@/services/looker/lookerAppLoaders';
import { loadCoverage } from '@/services/looker/lookerCoverageLoader';
import { fetchWorkspaceTargets } from '@/services/targets/targetsService';
import { CENTRAL_MAPPING, type ResultMappingInfo } from '@/services/targets/resultMetric';
import type { CprTargetRow } from '@/services/targets/targetRules';
import { loadFactPair, loadMapping, loadSummaries } from '@/services/overview/overviewLoaders';
import { overviewWindowsOf } from '@/services/overview/overviewWindows';
import type { FactPair, OverviewWindows, ReadState } from '@/services/overview/overviewTypes';
import type { SummaryReads } from '@/services/overview/topCampaigns';
import type { BackfillState } from './useLookerBackfill';
import type { ChannelFreshnessState } from './useLookerChannelFreshness';
import type { CoverageState } from './useLookerCoverage';
import { useKeyedRead } from './useKeyedRead';

/** workspaceId null = the master's all-workspaces view over `workspaceIds`. */
export interface OverviewScope { workspaceIds: string[]; workspaceId: string | null }

export interface OverviewData {
  /** null = no bounded range (an empty "this month", a refused custom range). */
  windows: OverviewWindows | null;
  facts: ReadState<FactPair>;
  summary: ReadState<SummaryReads>;
  /** null = still loading. */
  mapping: ResultMappingInfo | null;
  targets: ReadState<CprTargetRow[]>;
  coverage: CoverageState;
  backfill: BackfillState;
  freshness: ChannelFreshnessState;
}

const unscoped = (): Promise<never> => Promise.reject(new Error('overview: no scope'));

export const useOverviewData = (scope: OverviewScope | null, token: string): OverviewData => {
  const windows = useMemo(() => overviewWindowsOf(token, bangkokToday()), [token]);
  const ws = scope?.workspaceId ?? null;
  const ids = scope?.workspaceIds ?? [];
  const idsKey = ids.join(',');
  const range = (r: { start: string; end: string }): string => `${r.start}..${r.end}`;
  // I1: the previous window is part of the key: 'mtd' and an equal custom range share `current`
  // but not `previous`, so a key on `current` alone would keep stale previous rows (every Δ wrong).
  const base = scope && windows ? `${ws ?? 'all'}|${idsKey}|${range(windows.current)}|${range(windows.previous)}` : '';
  // M5: mapping, targets, backfill and freshness do not depend on the period; keyed on the workspace alone.
  const wsKey = scope && ws ? ws : '';

  const facts = useKeyedRead<FactPair>(base && `facts|${base}`, () => (windows ? loadFactPair(ids, windows) : unscoped()), 'OverviewFacts');
  const summary = useKeyedRead<SummaryReads>(base && `summary|${base}`, () => (windows ? loadSummaries(ids, windows) : unscoped()), 'OverviewSummary');
  const mappingRead = useKeyedRead<ResultMappingInfo>(wsKey && `map|${wsKey}`, () => (ws ? loadMapping(ws) : unscoped()), 'OverviewMapping');
  const targets = useKeyedRead<CprTargetRow[]>(wsKey && `targets|${wsKey}`,
    () => (ws ? fetchWorkspaceTargets(ws).then((t) => t.targets) : unscoped()), 'OverviewTargets');

  const mapping: ResultMappingInfo | null = !scope ? null : ws === null ? CENTRAL_MAPPING : mappingRead.status === 'ready' ? mappingRead.data : null;

  // Coverage reads the current window and follows the Awareness definition, so it waits for the mapping and re-reads when either changes.
  const coverageRead = useKeyedRead(wsKey && windows && mapping ? `cov|${wsKey}|${range(windows.current)}|aw:${String(mapping.awareness)}` : '',
    () => (ws && windows && mapping ? loadCoverage(ws, windows.current, mapping.awareness) : unscoped()), 'OverviewCoverage');
  const backfillRead = useKeyedRead(wsKey && `bf|${wsKey}`, () => (ws ? loadBackfill(ws, new Date()) : unscoped()), 'OverviewBackfill');
  const freshnessRead = useKeyedRead(wsKey && `fr|${wsKey}`, () => (ws ? loadChannelFreshness(ws) : unscoped()), 'OverviewFreshness');

  const coverage = useMemo<CoverageState>(() => {
    if (!scope) return { status: 'loading' };
    if (ws === null) return { status: 'idle', reason: 'allWorkspaces' };
    if (coverageRead.status === 'ready') return { status: 'ready', data: coverageRead.data };
    return coverageRead.status === 'failed' ? { status: 'failed' } : { status: 'loading' };
  }, [scope, ws, coverageRead]);

  const backfill = useMemo<BackfillState>(() => {
    if (backfillRead.status === 'ready') return { status: 'ready', ...backfillRead.data };
    return backfillRead.status === 'failed' ? { status: 'failed' } : backfillRead.status === 'loading' ? { status: 'loading' } : { status: 'idle' };
  }, [backfillRead]);

  const freshness = useMemo<ChannelFreshnessState>(() => {
    if (freshnessRead.status === 'ready') return { status: 'ready', channels: freshnessRead.data };
    return freshnessRead.status === 'failed' ? { status: 'failed' } : freshnessRead.status === 'loading' ? { status: 'loading' } : { status: 'idle' };
  }, [freshnessRead]);

  return useMemo(
    () => ({ windows, facts, summary, mapping, targets, coverage, backfill, freshness }),
    [windows, facts, summary, mapping, targets, coverage, backfill, freshness],
  );
};
